/* ==========================================================================
   ET — Menu 003: bestsellers prices with app discounts (A/B test)
   ==========================================================================
   The PDP price is rewritten client-side by the "Discounts Display Pro" app
   (automatic discounts, e.g. "38% extra KORTING": €39,95 → €24,77). The app
   deliberately skips anything inside <header>, <nav> and [role="dialog"], so
   it never touches the bestsellers cards in the mega menu / burger drawer.

   This script mirrors the app for those cards:
     - reads the app's own config (window["discounts-display-pro"]),
     - fetches the same /api/discounts endpoint for the menu product IDs,
     - picks the best automatic, one-time-purchase discount for the variant,
     - computes the price exactly like the app (percentage: floor of the
       saving, fixed: amount in cents), then shows the Liquid price struck
       through and the discounted price, like the PDP.
   Products without a live discount keep the Liquid-rendered price.

   Runs only while the reveal class `test-menu-003` is on <html> or <body>,
   and does nothing when the app is not installed.
   ========================================================================== */
(function () {
  const REVEAL_CLASS = 'test-menu-003';
  const CACHE_KEY = 'et-menu003-discounts';
  const CACHE_TTL = 5 * 60 * 1000;

  let started = false;

  function isRevealed() {
    return (
      document.documentElement.classList.contains(REVEAL_CLASS) ||
      (document.body && document.body.classList.contains(REVEAL_CLASS))
    );
  }

  function getApp() {
    const app = window['discounts-display-pro'];
    if (!app || !app.apiBaseUrl || !app.storefrontToken) return null;
    return app;
  }

  function sameId(a, b) {
    const x = String(a).match(/(\d+)$/);
    const y = String(b).match(/(\d+)$/);
    return !!(x && y && x[1] === y[1]);
  }

  function isEligible(discount, variantId) {
    if (!discount || !discount.isAutomatic) return false;
    if (discount.appliesOnOneTimePurchase === false) return false;
    if (discount.endDate && new Date(discount.endDate).getTime() < Date.now()) return false;

    const scope = discount.variantScope;
    if (scope && scope.type === 'PARTIAL') {
      return (scope.ids || []).some((id) => sameId(id, variantId));
    }
    return true;
  }

  /* Fixed amounts come in cents of the discount's currency; restate them in
     the shopper's currency the same way the app does. */
  function fixedAmount(discount, app) {
    const value = Number(discount.value);
    if (!Number.isFinite(value)) return null;

    const from = discount.currencyCode || app.baseCurrency;
    const shopifyCurrency = window.Shopify && window.Shopify.currency;
    const to = (shopifyCurrency && shopifyCurrency.active) || app.presentmentCurrency;
    if (!from || !to || from === to) return value;

    const rate = Number(shopifyCurrency && shopifyCurrency.rate);
    return Number.isFinite(rate) && rate > 0 ? Math.round(value * rate) : null;
  }

  function discountedPrice(price, discount, app) {
    if (discount.type === 'percentage') {
      const percent = Math.min(Math.max(Number(discount.value) || 0, 0), 100);
      return Math.max(0, price - Math.floor((price * percent) / 100));
    }
    if (discount.type === 'fixed') {
      const amount = fixedAmount(discount, app);
      return amount === null ? price : Math.max(0, price - Math.min(Math.max(amount, 0), price));
    }
    return price;
  }

  function bestPrice(price, variantId, discounts, app) {
    let best = price;
    (discounts || []).forEach((discount) => {
      if (!isEligible(discount, variantId)) return;
      best = Math.min(best, discountedPrice(price, discount, app));
    });
    return best;
  }

  function readCache(key) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cached && cached.key === key && Date.now() - cached.time < CACHE_TTL) return cached.data;
    } catch (e) {}
    return null;
  }

  function writeCache(key, data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ key: key, time: Date.now(), data: data }));
    } catch (e) {}
  }

  async function fetchDiscounts(app, productIds) {
    const key = productIds.slice().sort().join(',');
    const cached = readCache(key);
    if (cached) return cached;

    // Respect the app's own signals: no live discounts / rate-limit cooldown.
    if (app._envPromise) {
      const env = await app._envPromise;
      if (env && env.hasLiveDiscounts === false) return null;
    }
    if (app._noLiveDiscounts) return null;
    if (app._rateLimitedUntil && Date.now() < app._rateLimitedUntil) return null;

    const shop = app._shopDomain || (window.Shopify && window.Shopify.shop);
    if (!shop) return null;

    const params = new URLSearchParams({ shop: shop, productIds: productIds.join(',') });
    const response = await fetch(app.apiBaseUrl.replace(/\/$/, '') + '/api/discounts?' + params.toString(), {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + app.storefrontToken },
    });
    if (!response.ok) return null;

    const data = await response.json();
    writeCache(key, data);
    return data;
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && window.shopSettings) {
      return theme.Currency.formatMoney(cents, window.shopSettings.moneyFormat);
    }
    return null;
  }

  function span(className, text) {
    const el = document.createElement('span');
    el.className = className;
    el.textContent = text;
    return el;
  }

  function renderPrice(card, finalPrice, regularPrice) {
    const slot = card.querySelector('[data-et-menu003-slot]');
    const block = card.closest('[data-et-menu003]');
    const now = formatMoney(finalPrice);
    const was = formatMoney(regularPrice);
    if (!slot || !block || !now || !was) return;

    const wasEl = document.createElement('s');
    wasEl.className = 'et-menu003-bs__price-was';
    wasEl.textContent = was;

    slot.replaceChildren(
      span('visually-hidden', block.dataset.saleLabel || ''),
      span('et-menu003-bs__price-now', now),
      span('visually-hidden', block.dataset.regularLabel || ''),
      wasEl
    );
  }

  async function applyDiscounts() {
    const app = getApp();
    const cards = Array.from(document.querySelectorAll('[data-et-menu003-product]'));
    if (!app || cards.length === 0) return;

    const productIds = Array.from(new Set(cards.map((card) => card.dataset.etMenu003Product)));

    let data;
    try {
      data = await fetchDiscounts(app, productIds);
    } catch (e) {
      return;
    }
    if (!data || !data.products) return;

    cards.forEach((card) => {
      const product = data.products[card.dataset.etMenu003Product];
      if (!product || !Array.isArray(product.discounts) || product.discounts.length === 0) return;

      const price = Number(card.dataset.etMenu003Price);
      if (!(price > 0)) return;

      const finalPrice = bestPrice(price, card.dataset.etMenu003Variant, product.discounts, app);
      if (finalPrice < price) renderPrice(card, finalPrice, price);
    });
  }

  /* Burger drawer: product cards in a Swiper slider, 2 per view, with dots.
     The drawer is display:none on desktop; Swiper's resize observer lays the
     slider out once it becomes visible. */
  function initSliders() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('[data-et-menu003-slider]').forEach((el) => {
      if (el.swiper) return;
      const block = el.closest('[data-et-menu003]');

      new Swiper(el, {
        slidesPerView: 2,
        spaceBetween: 8,
        watchOverflow: true,
        pagination: {
          el: block && block.querySelector('[data-et-menu003-dots]'),
          clickable: true,
          bulletClass: 'et-menu003-bs__dot',
          bulletActiveClass: 'et-menu003-bs__dot--active',
        },
        a11y: { slideRole: 'listitem' },
      });
    });
  }

  function start() {
    if (started || !isRevealed()) return false;
    started = true;
    initSliders();
    applyDiscounts();
    return true;
  }

  /* Intelligems may add the reveal class after this deferred script runs. */
  function init() {
    if (start()) return;

    const observer = new MutationObserver(() => {
      if (start()) observer.disconnect();
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    if (document.body) observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
